import './styles/ShoppingCartItem.scss';

import { Close, OpenInNew } from '@mui/icons-material';
import { Card, CardHeader, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { Option, ProductType } from '@thro-types/leihbar';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { useShoppingCartItemMutation } from 'api/hooks/leihbar';
import { ResponsiveImage } from 'app/components/ResponsiveImage';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { formatToDateAndTime } from 'utils/dates';

import { ShoppingCartActions } from '../slice';
import { ShoppingCartItemWithAvailability } from '../types';

interface Props {
  item: ShoppingCartItemWithAvailability;
}
export const ShoppingCartItem = ({ item }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { update, deletion } = useShoppingCartItemMutation();

  const handleRemoveItemFromCart = async (item: ShoppingCartItemWithAvailability) => {
    try {
      await deletion.mutateAsync(item.id);
      dispatch(
        AlertActions.success(t(translations.pages.leihbar.shoppingCart.alerts.successfulUpdate()))
      );
    } catch (err) {
      dispatch(
        AlertActions.error(t(translations.pages.leihbar.shoppingCart.alerts.failedUpdate()))
      );
    }
  };

  const handleRemoveOptionFromCart = async (option: Option) => {
    try {
      await update.mutateAsync({
        id: item.id,
        data: {
          startTime: item.startTime,
          endTime: item.endTime,
          optionIds: item.options?.filter(o => o.id !== option.id).map(o => o.id) || [],
          amount: item.amount
        }
      });
      dispatch(
        AlertActions.success(t(translations.pages.leihbar.shoppingCart.alerts.successfulUpdate()))
      );
    } catch (err) {
      dispatch(
        AlertActions.error(t(translations.pages.leihbar.shoppingCart.alerts.failedUpdate()))
      );
    }
  };

  return (
    <Card raised sx={{ margin: '0 0 1em 0' }}>
      <CardHeader
        title={item.product?.name}
        action={
          <>
            <Tooltip title={t(translations.pages.leihbar.shoppingCart.tooltips.goToProduct())}>
              <IconButton
                aria-label="remove"
                onClick={e => {
                  navigate(`/leihbar/product/${item.product?.id}`);
                  dispatch(ShoppingCartActions.close());
                }}
              >
                <OpenInNew />
              </IconButton>
            </Tooltip>
            <IconButton
              aria-label="remove"
              onClick={e => {
                handleRemoveItemFromCart(item);
              }}
            >
              <Close />
            </IconButton>
          </>
        }
        titleTypographyProps={{ fontWeight: 'bold' }}
      />
      {item.unavailable && (
        <Grid item ml={3} style={{ opacity: '0.7' }}>
          {t(translations.pages.leihbar.shoppingCart.articleNotAvailable())}:
        </Grid>
      )}
      <Grid container className="shoppingcart-item">
        <Grid item xs alignSelf={'center'} marginRight={1}>
          <ResponsiveImage src={item.product?.image || ''} />
        </Grid>
        <Grid container item xs alignSelf={'center'}>
          <Typography fontWeight={'bold'} marginRight={1}>
            {t(translations.common.start())}:
          </Typography>
          {formatToDateAndTime(item.startTime)}
        </Grid>
        <Grid container item xs alignSelf={'center'}>
          <Typography fontWeight={'bold'} marginRight={1}>
            {t(translations.common.end())}:
          </Typography>
          {formatToDateAndTime(item.endTime)}
        </Grid>
        {item.comment && (
          <Grid container item xs={12} alignSelf={'center'}>
            <Typography fontWeight={'bold'} marginRight={1}>
              {t(translations.pages.leihbar.shoppingCart.comment())}:
            </Typography>
            {item.comment}
          </Grid>
        )}
      </Grid>
      {item.product?.productType === ProductType.BUNDLE && !!item.product?.products?.length ? (
        <>
          <Typography variant="h6" paddingLeft={'1em'}>
            {t(translations.pages.leihbar.shoppingCart.bundleContent())}:
          </Typography>
          <Grid container alignItems={'center'}>
            {item.product.products.map(product => (
              <Grid item xs={12} key={product.id}>
                <Card
                  raised
                  sx={{
                    margin: '0 1em 1em 1em',
                    minHeight: 120,
                    display: 'flex',
                    alignContent: 'center',
                    alignItems: 'center'
                  }}
                  key={product.id}
                >
                  <Grid container className="shoppingcart-item" gap={2}>
                    <Grid item xs={5} md={3} alignSelf={'center'}>
                      <ResponsiveImage src={product.image} />
                    </Grid>
                    <Grid item xs md style={{ alignSelf: 'center' }}>
                      <Typography variant="h5" fontWeight="bold">
                        {product.name}
                      </Typography>
                    </Grid>
                    {product.unavailable && (
                      <Grid item ml={3} style={{ opacity: '0.7' }}>
                        {t(translations.pages.leihbar.shoppingCart.articleNotAvailable())}:
                      </Grid>
                    )}
                  </Grid>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        <></>
      )}
      {!!item.options?.length && (
        <Typography variant="h6" paddingLeft={'1em'}>
          {t(translations.pages.leihbar.shoppingCart.options())}:
        </Typography>
      )}
      {item.options?.map(option => (
        <Card
          raised
          sx={{
            margin: '0 1em 1em 1em',
            display: 'flex',
            alignContent: 'center',
            alignItems: 'center'
          }}
          key={option.id}
        >
          <Grid container className="shoppingcart-item" gap={2}>
            <Grid item xs={5} md={3} lg={3} alignSelf={'center'}>
              <ResponsiveImage src={option.image} />
            </Grid>
            <Grid container item xs md lg justifyContent={'space-between'}>
              <Grid item xs md lg style={{ alignSelf: 'center' }}>
                <Typography variant="h5" fontWeight="bold">
                  {option.title}
                </Typography>
              </Grid>
              <Grid
                item
                xs
                md
                lg
                style={{
                  display: 'flex',
                  alignSelf: 'center',
                  justifyContent: 'flex-end'
                }}
              >
                <IconButton
                  aria-label="remove"
                  onClick={e => {
                    handleRemoveOptionFromCart(option);
                  }}
                  style={{ alignSelf: 'center' }}
                >
                  <Close />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        </Card>
      ))}
    </Card>
  );
};
